import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../ny.css';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { isThemeFlagOn } from '../gameSetupStorage';
import { getRunPlan } from '../game/stageContent';
import { buildAccentStyle } from '../game/stageShared';
import { askFeedback, nyAlert } from '../nyPopups';

/**
 * Results screen (reference screens 7–9):
 *   all points   → "New High Score!" + "Amazing!"
 *   ≥ half       → "Congratulations!"
 *   below half   → "Oh No! Better luck next time!"
 */

const FEEDBACK_OPTIONS = [
  { value: 'Amazingggg', emoji: '🤩', label: 'Amazing' },
  { value: 'Loved it', emoji: '😍', label: 'Loved it' },
  { value: 'It was fun', emoji: '😊', label: 'It was fun' },
  { value: 'It was okay', emoji: '👍', label: 'It was okay' },
];

const CONFETTI_COLORS = ['#ffc83d', '#ff5fa2', '#a66bff', '#4cc9ff', '#ffffff'];

function readThankYouUserId(user) {
  const fromUser = user?.userId ?? user?.id;
  if (fromUser != null && String(fromUser).trim() !== '') return String(fromUser).trim();
  try {
    const u = JSON.parse(sessionStorage.getItem('userData') || '{}');
    const id = u?.userId ?? u?.userid ?? u?.id;
    if (id != null && String(id).trim() !== '') return String(id).trim();
  } catch {
    /* ignore */
  }
  return '';
}

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 3.5 + Math.random() * 3,
        rotate: Math.random() * 360,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        wide: i % 3 === 0,
      })),
    []
  );
  return (
    <div className="ny-confetti" aria-hidden>
      {pieces.map((p, i) => (
        <span
          key={i}
          className={p.wide ? 'is-ribbon' : ''}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--r': `${p.rotate}deg`,
          }}
        />
      ))}
    </div>
  );
}

/** Sad disco ball for the "Oh No!" screen. */
function SadDiscoBall() {
  return (
    <svg className="ny-result-art" viewBox="0 0 200 190" role="img" aria-label="Sad disco ball">
      <defs>
        <radialGradient id="nyBall" cx="38%" cy="32%" r="70%">
          <stop offset="0%" stopColor="#f4f0ff" />
          <stop offset="45%" stopColor="#a99cf0" />
          <stop offset="100%" stopColor="#3b2f86" />
        </radialGradient>
        <clipPath id="nyBallClip">
          <circle cx="100" cy="92" r="70" />
        </clipPath>
      </defs>
      <ellipse cx="100" cy="176" rx="72" ry="10" fill="rgba(0,0,0,0.35)" />
      <circle cx="100" cy="92" r="70" fill="url(#nyBall)" />
      <g clipPath="url(#nyBallClip)" stroke="rgba(40,30,110,0.45)" strokeWidth="1.4" fill="none">
        {Array.from({ length: 9 }, (_, i) => (
          <line key={`h${i}`} x1="20" x2="180" y1={34 + i * 15} y2={34 + i * 15} />
        ))}
        {Array.from({ length: 11 }, (_, i) => (
          <path key={`v${i}`} d={`M${25 + i * 15} 20 Q${100 + (i - 5) * 22} 92 ${25 + i * 15} 164`} />
        ))}
      </g>
      <g fill="#ffffff" opacity="0.9">
        <rect x="58" y="46" width="10" height="10" rx="2" />
        <rect x="74" y="38" width="8" height="8" rx="2" />
        <rect x="128" y="58" width="9" height="9" rx="2" fill="#ffd6f0" />
      </g>
      <circle cx="78" cy="96" r="13" fill="#fff" />
      <circle cx="122" cy="96" r="13" fill="#fff" />
      <circle cx="80" cy="100" r="7" fill="#1d1f3a" />
      <circle cx="120" cy="100" r="7" fill="#1d1f3a" />
      <path d="M64 80 L88 86" stroke="#1d1f3a" strokeWidth="4" strokeLinecap="round" />
      <path d="M136 80 L112 86" stroke="#1d1f3a" strokeWidth="4" strokeLinecap="round" />
      <path d="M84 132 Q100 118 116 132" stroke="#1d1f3a" strokeWidth="4.5" fill="none" strokeLinecap="round" />
      <path d="M66 110 q-4 10 0 14 q4 -4 0 -14z" fill="#7cd3ff" />
      <path d="M100 14 L100 22" stroke="#cfc7ff" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

function ThankYou() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, ''),
    []
  );

  const nav = location.state;
  const [points, setPoints] = useState(typeof nav?.points === 'number' ? nav.points : 0);
  const [time, setTime] = useState(nav?.time != null && nav.time !== '' ? String(nav.time) : '');
  const [savedFeedback, setSavedFeedback] = useState('');
  const [reportLoaded, setReportLoaded] = useState(false);
  // 'feedback' → feedback popup is open; 'score' → result screen is shown.
  const [phase, setPhase] = useState('feedback');
  const askedRef = useRef(false);

  const viewerId = readThankYouUserId(user);
  const canSubmitFeedback = Boolean(viewerId && adminToken && backendBase);

  useEffect(() => {
    if (user?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [user, dispatch]);

  // Saved score/time/feedback from the report (survives a reload of this page).
  useEffect(() => {
    const uid = readThankYouUserId(user);
    if (!uid || !adminToken || !backendBase) {
      setReportLoaded(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: uid }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data.success || !data.report) return;
        const r = data.report;
        if (r.points !== undefined && r.points !== null) setPoints(Number(r.points) || 0);
        if (r.time != null && String(r.time).trim() !== '') setTime(String(r.time).trim());
        if (r.feedback != null && String(r.feedback).trim() !== '') {
          setSavedFeedback(String(r.feedback).trim());
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setReportLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken, backendBase]);

  const saveFeedback = useCallback(
    async (value) => {
      try {
        const res = await fetch(`${backendBase}/welcomeStageFeedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: viewerId, feedback: value }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Could not save feedback.');
        }
        setSavedFeedback(value);
      } catch (e) {
        console.error('saveFeedback:', e);
        await nyAlert({
          kind: 'error',
          title: 'Feedback not saved',
          text: e.message || 'Could not save feedback. Your score is still recorded.',
          confirmText: 'See my score',
        });
      }
    },
    [adminToken, backendBase, viewerId]
  );

  // 1) Feedback popup first (unless already given / not possible), 2) then the score screen.
  useEffect(() => {
    if (!reportLoaded || askedRef.current) return;
    askedRef.current = true;
    if (savedFeedback || !canSubmitFeedback) {
      setPhase('score');
      return;
    }
    (async () => {
      const value = await askFeedback({ options: FEEDBACK_OPTIONS });
      if (value) await saveFeedback(value);
      setPhase('score');
    })();
  }, [reportLoaded, savedFeedback, canSubmitFeedback, saveFeedback]);

  const { maxScore } = useMemo(() => getRunPlan(themeData), [themeData]);
  const ratio = maxScore > 0 ? points / maxScore : 0;
  const outcome = maxScore > 0 && points >= maxScore ? 'perfect' : ratio >= 0.5 ? 'win' : 'lose';

  const accentStyle = useMemo(() => buildAccentStyle(themeData), [themeData]);
  const showLeaderboard = isThemeFlagOn(themeData?.leaderboard, true);

  const headline = {
    perfect: 'New High Score!',
    win: 'Congratulations!',
    lose: 'Oh No!',
  }[outcome];

  // While the feedback popup is up, keep the page empty behind it.
  if (phase !== 'score') {
    return <div className="ny-page ny-result" style={accentStyle} />;
  }

  return (
    <div className={`ny-page ny-result ny-result--${outcome}`} style={accentStyle}>
      {outcome !== 'lose' ? <Confetti /> : null}

      <div className="ny-result-inner ny-content">
        <h1 className="ny-result-title">{headline}</h1>
        {outcome === 'lose' ? (
          <>
            <p className="ny-result-tagline">Better luck next time!</p>
            <SadDiscoBall />
          </>
        ) : null}

        <div className="ny-score-card">
          <p className="ny-score-label">You Scored</p>
          <div className="ny-score-value">
            <i className="fa-solid fa-star" aria-hidden />
            <span className="ny-score-points">{points}</span>
            {maxScore > 0 ? <span className="ny-score-max">/ {maxScore}</span> : null}
          </div>
          {outcome === 'perfect' ? <span className="ny-score-chip">Amazing!</span> : null}
          {time ? <p className="ny-score-time">Time: {time}</p> : null}
        </div>

        {/* The custom thank-you text is shown on /leaderboard's closing message instead.
            Admin → Rules → Show Leaderboard: on → ranking table, off → closing message.
            Both live on /leaderboard. */}
        <div className="ny-result-actions">
          <button
            type="button"
            className="ny-btn ny-btn--primary"
            onClick={() => navigate('/leaderboard')}
          >
            {showLeaderboard ? (
              <>
                <i className="fa-solid fa-trophy" aria-hidden />
                Leaderboard
              </>
            ) : (
              <>
                Next
                <i className="fa-solid fa-arrow-right" aria-hidden />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ThankYou;
